import { useState } from 'react';
import { useT } from '../../hooks/useT';
import Button from '../common/Button';

const DEBT_TYPES = [
  'credit_card',
  'personal_loan',
  'student_loan',
  'car_loan',
  'mortgage',
  'medical',
  'other',
];

const EMPTY = {
  title: '',
  debt_type: 'credit_card',
  total_debt: '',
  interest_rate: '',
  minimum_payment: '',
  description: '',
};

export default function DebtForm({ onSubmit, isSubmitting, initialValues, onUseSame }) {
  const t = useT();

  const [form, setForm] = useState(() => ({
    ...EMPTY,
    ...(initialValues
      ? {
          title:           initialValues.title           ?? '',
          debt_type:       initialValues.debt_type       ?? 'credit_card',
          total_debt:      initialValues.total_debt      != null ? String(initialValues.total_debt)      : '',
          interest_rate:   initialValues.interest_rate   != null ? String(initialValues.interest_rate)   : '',
          minimum_payment: initialValues.minimum_payment != null ? String(initialValues.minimum_payment) : '',
          description:     initialValues.description     ?? '',
        }
      : {}),
  }));

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  function handleSubmit(e) {
    e.preventDefault();
    onSubmit({
      ...form,
      total_debt:      parseFloat(form.total_debt),
      interest_rate:   parseFloat(form.interest_rate   || 0),
      minimum_payment: parseFloat(form.minimum_payment || 0),
    });
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {onUseSame && (
        <button
          type="button"
          onClick={onUseSame}
          className="w-full py-2.5 rounded-xl border border-dashed border-slate-600 text-slate-400 text-sm hover:border-brand-500 hover:text-brand-400 transition-colors"
        >
          {t('regen_modal.use_same')}
        </button>
      )}

      <div>
        <label className="label">{t('debt.form.title')}</label>
        <input
          className="input"
          placeholder={t('debt.form.title_placeholder')}
          value={form.title}
          onChange={set('title')}
          required
          maxLength={255}
        />
      </div>

      <div>
        <label className="label">{t('debt.form.debt_type')}</label>
        <select className="input" value={form.debt_type} onChange={set('debt_type')}>
          {DEBT_TYPES.map((type) => (
            <option key={type} value={type}>
              {t(`debt.type.${type}`)}
            </option>
          ))}
        </select>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="label">{t('debt.form.total_debt')}</label>
          <input
            className="input"
            type="number"
            step="0.01"
            min="0.01"
            placeholder="0.00"
            value={form.total_debt}
            onChange={set('total_debt')}
            required
          />
        </div>
        <div>
          <label className="label">{t('debt.form.interest_rate')}</label>
          <input
            className="input"
            type="number"
            step="0.01"
            min="0"
            max="200"
            placeholder="e.g. 18.99"
            value={form.interest_rate}
            onChange={set('interest_rate')}
          />
        </div>
      </div>

      <div>
        <label className="label">{t('debt.form.minimum_payment')}</label>
        <input
          className="input"
          type="number"
          step="0.01"
          min="0"
          placeholder="0.00"
          value={form.minimum_payment}
          onChange={set('minimum_payment')}
        />
        <p className="text-xs text-slate-500 mt-1">{t('debt.form.minimum_payment_hint')}</p>
      </div>

      <div>
        <label className="label">{t('debt.form.description')}</label>
        <textarea
          className="input resize-none"
          rows={2}
          placeholder={t('debt.form.description_placeholder')}
          value={form.description}
          onChange={set('description')}
          maxLength={500}
        />
      </div>

      <Button type="submit" disabled={isSubmitting} className="w-full">
        {isSubmitting ? t('common.saving') : t('debt.form.submit')}
      </Button>
    </form>
  );
}
